import {
  scenarioLayersApi,
  VolumetricEstimate,
} from '@/api/scenarioLayers';
import {
  AREA_PROPERTY_NAME,
  calculateVolumetricGeological,
  clearVolumetricColumn,
  copyVolumetricColumn,
  emptyVolumetricDraft,
  mergePrefillIntoDraft,
  OIL_DENSITY_PROPERTY_NAME,
  VolumetricEstimateDraft,
  VOLUMETRIC_PARAMETERS,
  VOLUMETRIC_POINT_COUNT,
  VolumetricParameterKey,
  buildPropertyValueMap,
  validateVolumetricInput,
} from '@/utils/volumetricEstimation';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DECIMAL_REGEX = /^-?\d*(\.\d{0,3})?$/;
const PARTIAL_DECIMAL = /^\.$|^-?\d+\.$/;

interface LayerPropertyLike {
  attribute_name: string;
  float_value?: number;
  integer_value?: number;
  attribute_unit?: string | null;
}

interface VolumetricEstimateDialogProps {
  open: boolean;
  onClose: () => void;
  scenarioLayerId?: string | null;
  layerProperties: LayerPropertyLike[];
  draft?: VolumetricEstimateDraft | null;
  onDraftChange?: (draft: VolumetricEstimateDraft) => void;
  onApply: (totalThousandTons: number, draft: VolumetricEstimateDraft) => void;
}

function estimateToDraft(estimate: VolumetricEstimate): VolumetricEstimateDraft {
  const points = Array.from({ length: VOLUMETRIC_POINT_COUNT }, (_, i) => {
    const p = estimate.points.find((x) => x.column_index === i);
    return {
      column_index: i,
      gross_thickness_m: p?.gross_thickness_m ?? null,
      net_to_gross_pct: p?.net_to_gross_pct ?? null,
      porosity_pct: p?.porosity_pct ?? null,
      water_saturation_pct: p?.water_saturation_pct ?? null,
      oil_fvf: p?.oil_fvf ?? null,
      geological_thousand_tons: p?.geological_thousand_tons ?? null,
    };
  });
  return {
    area_km2: estimate.area_km2 ?? null,
    oil_density_g_cm3: estimate.oil_density_g_cm3 ?? null,
    points,
    total_geological_thousand_tons: estimate.total_geological_thousand_tons ?? null,
  };
}

function payloadFromDraft(draft: VolumetricEstimateDraft) {
  return {
    area_km2: draft.area_km2 ?? null,
    oil_density_g_cm3: draft.oil_density_g_cm3 ?? null,
    points: draft.points.map((p) => ({
      gross_thickness_m: p.gross_thickness_m ?? null,
      net_to_gross_pct: p.net_to_gross_pct ?? null,
      porosity_pct: p.porosity_pct ?? null,
      water_saturation_pct: p.water_saturation_pct ?? null,
      oil_fvf: p.oil_fvf ?? null,
    })),
    total_geological_thousand_tons: draft.total_geological_thousand_tons ?? null,
  };
}

export const VolumetricEstimateDialog: React.FC<VolumetricEstimateDialogProps> = ({
  open,
  onClose,
  scenarioLayerId,
  layerProperties,
  draft: externalDraft,
  onDraftChange,
  onApply,
}) => {
  const [draft, setDraft] = useState<VolumetricEstimateDraft>(emptyVolumetricDraft());
  const [cellDrafts, setCellDrafts] = useState<Record<string, string>>({});
  const [areaDraft, setAreaDraft] = useState('');
  const [densityDraft, setDensityDraft] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [copyMenu, setCopyMenu] = useState<{ anchor: HTMLElement; targetCol: number } | null>(null);
  const initializedForOpenRef = useRef(false);

  const propertiesByName = useMemo(
    () => buildPropertyValueMap(layerProperties),
    [layerProperties],
  );

  const syncDraftsFromState = useCallback((next: VolumetricEstimateDraft) => {
    const drafts: Record<string, string> = {};
    VOLUMETRIC_PARAMETERS.forEach((param) => {
      for (let col = 0; col < VOLUMETRIC_POINT_COUNT; col += 1) {
        const val = next.points[col]?.[param.key as VolumetricParameterKey];
        drafts[`${param.key}:${col}`] = val == null ? '' : String(val);
      }
    });
    setCellDrafts(drafts);
    setAreaDraft(next.area_km2 == null ? '' : String(next.area_km2));
    setDensityDraft(next.oil_density_g_cm3 == null ? '' : String(next.oil_density_g_cm3));
  }, []);

  const updateDraft = useCallback(
    (next: VolumetricEstimateDraft) => {
      setDraft(next);
      onDraftChange?.(next);
    },
    [onDraftChange],
  );

  useEffect(() => {
    if (!open) {
      initializedForOpenRef.current = false;
      return;
    }
    if (initializedForOpenRef.current) return;
    initializedForOpenRef.current = true;

    const init = async () => {
      setErrors([]);
      setLoading(true);
      try {
        if (externalDraft) {
          const merged = mergePrefillIntoDraft(externalDraft, propertiesByName, layerProperties);
          updateDraft(merged);
          syncDraftsFromState(merged);
          return;
        }
        if (scenarioLayerId) {
          const saved = await scenarioLayersApi.getVolumetricEstimate(scenarioLayerId);
          if (saved) {
            const next = estimateToDraft(saved);
            updateDraft(next);
            syncDraftsFromState(next);
            return;
          }
        }
        const fresh = mergePrefillIntoDraft(emptyVolumetricDraft(), propertiesByName, layerProperties);
        updateDraft(fresh);
        syncDraftsFromState(fresh);
      } catch (e: unknown) {
        setErrors([e instanceof Error ? e.message : 'Failed to load volumetric estimate']);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [open, scenarioLayerId, externalDraft, propertiesByName, syncDraftsFromState, updateDraft]);

  const parseDraftNumber = (raw: string): number | null => {
    if (raw === '' || raw === '.' || raw === '-' || raw === '-.' || PARTIAL_DECIMAL.test(raw)) {
      return null;
    }
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleAreaChange = (raw: string) => {
    if (!DECIMAL_REGEX.test(raw)) return;
    setAreaDraft(raw);
    const area = parseDraftNumber(raw);
    updateDraft({
      ...draft,
      area_km2: area,
      total_geological_thousand_tons: null,
    });
  };

  const handleDensityChange = (raw: string) => {
    if (!DECIMAL_REGEX.test(raw)) return;
    setDensityDraft(raw);
    const density = parseDraftNumber(raw);
    updateDraft({
      ...draft,
      oil_density_g_cm3: density,
      total_geological_thousand_tons: null,
    });
  };

  const handleCellChange = (key: VolumetricParameterKey, col: number, raw: string) => {
    if (!DECIMAL_REGEX.test(raw)) return;
    const cellKey = `${key}:${col}`;
    setCellDrafts((prev) => ({ ...prev, [cellKey]: raw }));
    const parsed = parseDraftNumber(raw);
    const points = draft.points.map((p, idx) =>
      idx === col
        ? { ...p, [key]: parsed }
        : p,
    );
    updateDraft({ ...draft, points, total_geological_thousand_tons: null });
    setErrors([]);
  };

  const handleCopyColumn = (fromCol: number, toCol: number) => {
    const next = copyVolumetricColumn(draft, fromCol, toCol);
    updateDraft(next);
    syncDraftsFromState(next);
    setErrors([]);
    setCopyMenu(null);
  };

  const handleClearColumn = (col: number) => {
    const next = clearVolumetricColumn(draft, col);
    updateDraft(next);
    syncDraftsFromState(next);
    setErrors([]);
  };

  const runCalculate = async () => {
    const validation = validateVolumetricInput(draft.area_km2, draft.oil_density_g_cm3, draft.points);
    if (validation.length) {
      setErrors(validation);
      return;
    }

    setCalculating(true);
    setErrors([]);
    try {
      const area = Number(draft.area_km2);
      const density = Number(draft.oil_density_g_cm3);
      if (scenarioLayerId) {
        const result = await scenarioLayersApi.calculateVolumetricEstimate(
          scenarioLayerId,
          payloadFromDraft(draft),
        );
        const points = draft.points.map((p) => {
          const hit = result.points.find((r) => r.column_index === p.column_index);
          return hit ? { ...p, geological_thousand_tons: hit.geological_thousand_tons } : p;
        });
        const next = {
          ...draft,
          points,
          total_geological_thousand_tons: result.total_geological_thousand_tons,
        };
        updateDraft(next);
      } else {
        const result = calculateVolumetricGeological(area, density, draft.points);
        const points = draft.points.map((p) => {
          const hit = result.points.find((r) => r.column_index === p.column_index);
          return hit
            ? { ...p, geological_thousand_tons: hit.geological_thousand_tons }
            : { ...p, geological_thousand_tons: null };
        });
        updateDraft({
          ...draft,
          points,
          total_geological_thousand_tons: result.total_geological_thousand_tons,
        });
      }
    } catch (e: unknown) {
      const err = e as { status_code?: number; detail?: string | string[] };
      const detail = err.detail;
      if (Array.isArray(detail)) setErrors(detail);
      else if (typeof detail === 'string') setErrors([detail]);
      else setErrors([e instanceof Error ? e.message : 'Calculation failed']);
    } finally {
      setCalculating(false);
    }
  };

  const handleApply = async () => {
    if (draft.total_geological_thousand_tons == null) {
      setErrors(['Calculate geological oil before applying the result.']);
      return;
    }

    setApplying(true);
    setErrors([]);
    try {
      const total = draft.total_geological_thousand_tons;
      if (scenarioLayerId) {
        await scenarioLayersApi.applyVolumetricEstimate(scenarioLayerId, payloadFromDraft(draft));
      }
      onApply(total, draft);
      onClose();
    } catch (e: unknown) {
      const err = e as { detail?: string | string[] };
      const detail = err.detail;
      if (Array.isArray(detail)) setErrors(detail);
      else if (typeof detail === 'string') setErrors([detail]);
      else setErrors([e instanceof Error ? e.message : 'Failed to apply result']);
    } finally {
      setApplying(false);
    }
  };

  const resultByColumn = useMemo(() => {
    const map = new Map<number, number>();
    draft.points.forEach((p) => {
      if (p.geological_thousand_tons != null) map.set(p.column_index, p.geological_thousand_tons);
    });
    return map;
  }, [draft.points]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Volumetric estimate — initial geological oil</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          SPE bulk-volume method: geological oil [thousand t] = Area × gross thickness × NtG × φ × (1 − Sw) / Bo × 1000 × ρ.
          Layer area and oil density are shared; petrophysical parameters are entered per measurement point.
          Active columns split area equally.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Mapped area (Oil and gas area, km2)"
            value={areaDraft}
            onChange={(e) => handleAreaChange(e.target.value)}
            helperText={`Layer-level area, km2. Prefilled from "${AREA_PROPERTY_NAME}" when available.`}
            sx={{ maxWidth: 360 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Oil density at surface conditions"
            value={densityDraft}
            onChange={(e) => handleDensityChange(e.target.value)}
            helperText={`g/cm³. Prefilled from "${OIL_DENSITY_PROPERTY_NAME}" when available.`}
            sx={{ maxWidth: 360 }}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.map((err) => (
              <div key={err}>{err}</div>
            ))}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220, fontWeight: 600 }}>Parameter</TableCell>
                  {Array.from({ length: VOLUMETRIC_POINT_COUNT }, (_, i) => (
                    <TableCell key={i} align="center" sx={{ minWidth: 96, fontWeight: 600, verticalAlign: 'top' }}>
                      <Typography variant="caption" display="block" fontWeight={600}>
                        Point {i + 1}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.25 }}>
                        <Tooltip title="Copy values from another point">
                          <IconButton
                            size="small"
                            aria-label={`Copy values into point ${i + 1}`}
                            onClick={(e) => setCopyMenu({ anchor: e.currentTarget, targetCol: i })}
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Clear entire column">
                          <IconButton
                            size="small"
                            aria-label={`Clear point ${i + 1}`}
                            onClick={() => handleClearColumn(i)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {VOLUMETRIC_PARAMETERS.map((param) => (
                  <TableRow key={param.key}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{param.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {param.unit}{param.hint ? ` · ${param.hint}` : ''}
                      </Typography>
                    </TableCell>
                    {Array.from({ length: VOLUMETRIC_POINT_COUNT }, (_, col) => (
                      <TableCell key={col} align="center" sx={{ p: 0.5 }}>
                        <TextField
                          size="small"
                          value={cellDrafts[`${param.key}:${col}`] ?? ''}
                          onChange={(e) =>
                            handleCellChange(param.key as VolumetricParameterKey, col, e.target.value)
                          }
                          inputProps={{ style: { textAlign: 'center' } }}
                          sx={{ width: 76 }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>Geological oil (thousand t)</Typography>
                  </TableCell>
                  {Array.from({ length: VOLUMETRIC_POINT_COUNT }, (_, col) => (
                    <TableCell key={col} align="center">
                      {resultByColumn.has(col)
                        ? resultByColumn.get(col)!.toLocaleString('en-GB', { maximumFractionDigits: 3 })
                        : '—'}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Menu
          anchorEl={copyMenu?.anchor ?? null}
          open={copyMenu != null}
          onClose={() => setCopyMenu(null)}
        >
          {copyMenu != null &&
            Array.from({ length: VOLUMETRIC_POINT_COUNT }, (_, fromCol) => fromCol)
              .filter((fromCol) => fromCol !== copyMenu.targetCol)
              .map((fromCol) => (
                <MenuItem key={fromCol} onClick={() => handleCopyColumn(fromCol, copyMenu.targetCol)}>
                  Copy from Point {fromCol + 1}
                </MenuItem>
              ))}
        </Menu>

        {draft.total_geological_thousand_tons != null && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Total initial geological oil:{' '}
            <strong>
              {draft.total_geological_thousand_tons.toLocaleString('en-GB', { maximumFractionDigits: 3 })}
            </strong>{' '}
            thousand t
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={runCalculate} disabled={loading || calculating}>
          {calculating ? 'Calculating…' : 'Calculate'}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={loading || applying || draft.total_geological_thousand_tons == null}
        >
          {applying ? 'Saving…' : 'Apply to initial geological'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
